import * as React from 'react';
import moment from 'moment-timezone';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { ProductContainer, productStatuses, InputField, TabType } from './helpers';
import { Form } from '../utils/inputs';
import { useProductDetails } from './hooks/useProductDetails';
import { wfpFormat } from '../utils/utils';
import { Select } from '../utils/form/fields/Select';
import { hasFeatureAccess } from '../login/auth';
import { ManagerPermission } from '../permission-profiles/permission';
import State from '../app/store/state';
import { ConfirmationDialog } from '../utils/Dialogs';
import { authorizeProduct } from '../apiClient';
import { errorAction, successAction } from '../utils/notifications';

export const ProductsDetails: React.FC = function ProductsDetails() {
    const params = useParams<{ productId: string }>();

    const {
        editing,
        product,
        productImage,
        productId,
        toggleEdit,
        handleSubmit,
        handleChange,
        handleMultiSelectChange,
        handleMaxAllowedQuantityToggle,
        refreshProduct,
        categories,
        units,
        quantityOptions,
        limitationGroups,
        tz,
        hasPendingImageUpdate,
        setStagedImageKey,
        stagedImagePreviewUrl,
        setStagedImagePreviewUrl,
    } = useProductDetails(params as { productId: string });
    const auth = useSelector((state: State) => state.auth);
    const dispatch = useDispatch();
    const [showAuthorizeConfirm, setShowAuthorizeConfirm] = React.useState(false);
    const [showParkConfirm, setShowParkConfirm] = React.useState(false);
    const [authorizing, setAuthorizing] = React.useState(false);
    const [uploading, setUploading] = React.useState(false);
    const [pendingImageFile, setPendingImageFile] = React.useState<File | null>(null);
    const [pendingImagePreviewUrl, setPendingImagePreviewUrl] = React.useState<string | null>(null);
    const [showImageConfirm, setShowImageConfirm] = React.useState(false);

    const canEdit = hasFeatureAccess(auth, ManagerPermission.productsCreateUpdate);
    const canAuthorize =
        hasFeatureAccess(auth, ManagerPermission.productsCreateUpdate) &&
        !product?.authorizedAt &&
        String(product?.createdByManagerId) !== String(auth.manager?.id);

    const onAuthorize = async () => {
        if (!product) return;
        setAuthorizing(true);
        try {
            await authorizeProduct(product.id as number);
            refreshProduct();
            dispatch(successAction('Product authorized successfully'));
        } finally {
            setAuthorizing(false);
            setShowAuthorizeConfirm(false);
        }
    };

    if (!product) return <div>loading...</div>;

    return (
        <ProductContainer activeTab={TabType.main} product={product} productId={productId}>
            <div>
                <div style={{ marginBottom: '2rem' }}>
                    <h6>Product Image</h6>
                    {!hasPendingImageUpdate && productImage ? (
                        <>
                            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
                                <img
                                    alt={product.name}
                                    src={productImage.url}
                                    style={{
                                        maxWidth: '300px',
                                        maxHeight: '300px',
                                        objectFit: 'contain',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        padding: '8px',
                                    }}
                                />
                            </div>
                            <p style={{ textAlign: 'center', fontSize: '0.9em', color: '#666' }}>
                                Version: {productImage.version}
                            </p>
                        </>
                    ) : (
                        <div style={{ textAlign: 'center', color: '#666', marginBottom: '1rem' }}>
                            {hasPendingImageUpdate
                                ? 'There is a pending image update request'
                                : 'No image uploaded yet'}
                        </div>
                    )}
                    {editing && stagedImagePreviewUrl && (
                        <div style={{ marginTop: '1rem' }}>
                            <div style={{ textAlign: 'center', marginBottom: '0.5rem', fontWeight: 600 }}>
                                Staged image preview
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                                <img
                                    alt={`${product.name} staged`}
                                    src={stagedImagePreviewUrl}
                                    style={{
                                        maxWidth: '300px',
                                        maxHeight: '300px',
                                        objectFit: 'contain',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        padding: '8px',
                                    }}
                                />
                            </div>
                        </div>
                    )}
                    {editing && (
                        <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                            <label className="wfp-btn--primary" style={{ cursor: 'pointer' }}>
                                {uploading ? 'Uploading...' : productImage ? 'Upload New Image' : 'Upload Image'}
                                <input
                                    accept="image/jpeg,image/jpg"
                                    onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
                                        const inputEl = e.currentTarget;
                                        const file = inputEl.files?.[0];
                                        if (!file) return;
                                        const TEN_MB_BYTES = 10 * 1024 * 1024;
                                        if (file.size > TEN_MB_BYTES) {
                                            // reset file input and previews
                                            inputEl.value = '';
                                            dispatch(errorAction('Image must be smaller than 10MB'));
                                            if (pendingImagePreviewUrl) URL.revokeObjectURL(pendingImagePreviewUrl);
                                            setPendingImagePreviewUrl(null);
                                            setPendingImageFile(null);
                                            setStagedImagePreviewUrl(null);
                                            setShowImageConfirm(false);
                                            return;
                                        }
                                        // Stage file and open confirmation modal with preview
                                        setPendingImageFile(file);
                                        const previewUrl = URL.createObjectURL(file);
                                        setPendingImagePreviewUrl(previewUrl);
                                        setStagedImagePreviewUrl(previewUrl);
                                        setShowImageConfirm(true);
                                        // Do not auto-upload yet; wait for confirmation
                                    }}
                                    style={{ display: 'none' }}
                                    type="file"
                                />
                            </label>
                        </div>
                    )}
                </div>

                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '1rem',
                    }}
                >
                    <h6>General Information</h6>
                    <div>
                        {canAuthorize && (
                            <button
                                className="wfp-btn--primary mr2"
                                disabled={authorizing}
                                onClick={() => setShowAuthorizeConfirm(true)}
                                type="button"
                            >
                                {authorizing ? 'Authorizing...' : 'Authorize'}
                            </button>
                        )}
                        {canEdit &&
                            (editing ? (
                                <button className="wfp-btn--primary" onClick={toggleEdit} type="button">
                                    Cancel
                                </button>
                            ) : (
                                <button
                                    className="wfp-btn--primary"
                                    disabled={!product?.authorizedAt}
                                    onClick={toggleEdit}
                                    title={
                                        !product?.authorizedAt ? 'Product must be authorized before editing' : undefined
                                    }
                                    type="button"
                                >
                                    Edit
                                </button>
                            ))}
                    </div>
                </div>

                <Form className="wfp-form" onSubmit={handleSubmit}>
                    <div className="wfp-form--group row">
                        <label className="col-sm-4 ta-right">Name</label>
                        <InputField
                            autoFocused={true}
                            maxLength={35}
                            name="name"
                            onChange={handleChange}
                            readonly={!editing}
                            required={true}
                            type="text"
                            value={product.name}
                        />
                    </div>
                    <div className="wfp-form--group row">
                        <label className="col-sm-4 ta-right">Category</label>
                        <div className="col-sm-8">
                            {editing ? (
                                <Select
                                    name="category"
                                    onChange={handleChange}
                                    options={categories}
                                    value={product.category}
                                />
                            ) : (
                                wfpFormat(product.category)
                            )}
                        </div>
                    </div>
                    <div className="wfp-form--group row">
                        <label className="col-sm-4 ta-right">Quantity Option</label>
                        <div className="col-sm-8">
                            {editing ? (
                                quantityOptions.length > 0 ? (
                                    <Select
                                        disabled={product.maxAllowedQuantity === 1}
                                        name="quantityOption"
                                        onChange={handleChange}
                                        options={quantityOptions}
                                        value={product.quantityOption}
                                    />
                                ) : (
                                    <div>Loading options...</div>
                                )
                            ) : (
                                wfpFormat(product.quantityOption)
                            )}
                        </div>
                    </div>
                    <div className="wfp-form--group row">
                        <label className="col-sm-4 ta-right">Unit</label>
                        <div className="col-sm-8">
                            {editing ? (
                                units.length > 0 ? (
                                    <Select name="unit" onChange={handleChange} options={units} value={product.unit} />
                                ) : (
                                    <div>Loading options...</div>
                                )
                            ) : (
                                wfpFormat(product.unit)
                            )}
                        </div>
                    </div>
                    <div className="wfp-form--group row">
                        <label className="col-sm-4 ta-right">Allow only 1 quantity</label>
                        <div className="col-sm-8">
                            {editing ? (
                                <label className="wfp-form">
                                    <input
                                        checked={product.maxAllowedQuantity === 1}
                                        className="wfp-checkbox"
                                        onChange={(e) => handleMaxAllowedQuantityToggle(e.target.checked)}
                                        type="checkbox"
                                    />
                                </label>
                            ) : product.maxAllowedQuantity === 1 ? (
                                <label className="normal">Yes (1)</label>
                            ) : (
                                <label className="normal">No (0)</label>
                            )}
                        </div>
                    </div>
                    <div className="wfp-form--group row">
                        <label className="col-sm-4 ta-right">Status</label>
                        <div className="col-sm-8">
                            {editing ? (
                                <Select
                                    name="status"
                                    onChange={handleChange}
                                    options={productStatuses}
                                    value={product.status}
                                />
                            ) : (
                                wfpFormat(product.status)
                            )}
                        </div>
                    </div>
                    <div className="wfp-form--group row">
                        <label className="col-sm-4 ta-right">Groups</label>
                        <div className="col-sm-8">
                            {editing ? (
                                <Select
                                    multi={true}
                                    name="productLimitationGroups"
                                    onChange={handleMultiSelectChange}
                                    options={limitationGroups}
                                    placeholder="Select groups (required)"
                                    value={
                                        product.groups
                                            ? limitationGroups.filter((group) =>
                                                  product.groups?.map(String).includes(group.value)
                                              )
                                            : []
                                    }
                                />
                            ) : (
                                <label className="normal">
                                    {product.groups && product.groups.length > 0
                                        ? limitationGroups
                                              .filter((group) => product.groups?.map(String).includes(group.value))
                                              .map((group) => group.label)
                                              .join(', ')
                                        : '-'}
                                </label>
                            )}
                        </div>
                    </div>
                    <div className="wfp-form--group row">
                        <label className="col-sm-4 ta-right">Translation</label>
                        <InputField
                            maxLength={100}
                            name="translation"
                            onChange={handleChange}
                            readonly={!editing}
                            required={false}
                            type="text"
                            value={product.translation || ''}
                        />
                    </div>
                    <div className="wfp-form--group row">
                        <label className="col-sm-4 ta-right">Translation (English)</label>
                        <InputField
                            maxLength={100}
                            name="translationEn"
                            onChange={handleChange}
                            readonly={!editing}
                            required={false}
                            type="text"
                            value={product.translationEn || ''}
                        />
                    </div>
                    <div className="wfp-form--group row">
                        <label className="col-sm-4 ta-right">Created By</label>
                        <div className="col-sm-8">
                            <label className="normal">{product.createdByManager}</label>
                        </div>
                    </div>
                    <div className="wfp-form--group row">
                        <label className="col-sm-4 ta-right">Created At</label>
                        <div className="col-sm-8">
                            <label className="normal">
                                {moment(product.createdAt).tz(tz).format('DD/MM/YYYY HH:mm')}
                            </label>
                        </div>
                    </div>
                    <div className="wfp-form--group row">
                        <label className="col-sm-4 ta-right">Authorized At</label>
                        <div className="col-sm-8">
                            <label className="normal">
                                {product.authorizedAt
                                    ? moment(product.authorizedAt).tz(tz).format('DD/MM/YYYY HH:mm')
                                    : '-'}
                            </label>
                        </div>
                    </div>
                    {editing && (
                        <div className="wfp-form--actions text-center">
                            <div className="col-sm-8 col-sm-offset-4">
                                <button
                                    className="wfp-btn--primary"
                                    onClick={() => setShowParkConfirm(true)}
                                    type="button"
                                >
                                    Park Changes
                                </button>
                            </div>
                        </div>
                    )}
                </Form>
                {showAuthorizeConfirm && (
                    <ConfirmationDialog
                        message="Are you sure you want to authorize this product?"
                        onClose={() => setShowAuthorizeConfirm(false)}
                        onConfirm={onAuthorize}
                        title="Confirm Authorization"
                    />
                )}
                {showParkConfirm && (
                    <ConfirmationDialog
                        message="Are you sure you want to park changes to this product?"
                        onClose={() => setShowParkConfirm(false)}
                        onConfirm={() => {
                            setShowParkConfirm(false);
                            handleSubmit();
                        }}
                        title="Confirm Park"
                    />
                )}
                {showImageConfirm && (
                    <ConfirmationDialog
                        message={
                            <div>
                                <div
                                    style={{
                                        display: 'flex',
                                        gap: 16,
                                        justifyContent: 'center',
                                        alignItems: 'flex-start',
                                        flexWrap: 'wrap',
                                    }}
                                >
                                    <div style={{ textAlign: 'center' }}>
                                        <div style={{ fontWeight: 600, marginBottom: 8 }}>Current</div>
                                        {productImage ? (
                                            <img
                                                alt={`${product.name} current`}
                                                src={productImage.url}
                                                style={{
                                                    maxWidth: 240,
                                                    maxHeight: 240,
                                                    objectFit: 'contain',
                                                    border: '1px solid #ddd',
                                                    borderRadius: 4,
                                                    padding: 8,
                                                }}
                                            />
                                        ) : (
                                            <div
                                                style={{
                                                    width: 240,
                                                    height: 240,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    border: '1px dashed #ccc',
                                                    color: '#666',
                                                }}
                                            >
                                                No image
                                            </div>
                                        )}
                                    </div>
                                    <div style={{ textAlign: 'center' }}>
                                        <div style={{ fontWeight: 600, marginBottom: 8 }}>New</div>
                                        {pendingImagePreviewUrl ? (
                                            <img
                                                alt={`${product.name} new`}
                                                src={pendingImagePreviewUrl}
                                                style={{
                                                    maxWidth: 240,
                                                    maxHeight: 240,
                                                    objectFit: 'contain',
                                                    border: '1px solid #ddd',
                                                    borderRadius: 4,
                                                    padding: 8,
                                                }}
                                            />
                                        ) : null}
                                    </div>
                                </div>
                                <p style={{ marginTop: 16, color: '#666', textAlign: 'center' }}>
                                    Do you want to replace the current image with the new one?
                                </p>
                            </div>
                        }
                        onClose={() => {
                            setShowImageConfirm(false);
                            // Revoke and clear the staged file and preview
                            if (pendingImagePreviewUrl) URL.revokeObjectURL(pendingImagePreviewUrl);
                            setPendingImagePreviewUrl(null);
                            setPendingImageFile(null);
                        }}
                        onConfirm={async () => {
                            if (!pendingImageFile) return;
                            setUploading(true);
                            try {
                                const { stageProductImage } = await import('../apiClient');
                                const stageRes = await stageProductImage(productId, pendingImageFile as File);
                                const stagedKey: string | undefined = (stageRes as { key?: string })?.key;
                                if (stagedKey) setStagedImageKey(stagedKey);
                                dispatch(successAction('Image staged. Click "Park Changes" to create update request.'));
                            } finally {
                                setUploading(false);
                                setShowImageConfirm(false);
                                if (pendingImagePreviewUrl) URL.revokeObjectURL(pendingImagePreviewUrl);
                                setPendingImagePreviewUrl(null);
                                setPendingImageFile(null);
                            }
                        }}
                        title="Confirm Image Change"
                    />
                )}
            </div>
        </ProductContainer>
    );
};
